import { assertUnreachable, UIActionsContext } from '@tableau/data-catalog-shared';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets'
import * as clipboard from 'clipboard-polyfill';
import React = require('react');
import { Messages } from '../../../gen-src/Localize';
import { CustomSqlFragment_fields_ColumnField_columns_table_CustomSQLTable } from '../../../gen-src/queries/CustomSqlFragment';
import { FlowCustomSqlQuery_flows } from '../../../gen-src/queries/FlowCustomSqlQuery';
import { PublishedDatasourceCustomSqlQuery_publishedDatasources } from '../../../gen-src/queries/PublishedDatasourceCustomSqlQuery';
import { VirtualConnectionQuery_virtualConnections } from '../../../gen-src/queries/VirtualConnectionQuery';
import { WorkbookCustomSqlQuery_workbooks } from '../../../gen-src/queries/WorkbookCustomSqlQuery';
import {
  codeTextClass, customSqlCopyButtonStyle, customSqlDialogHeight, customSqlDialogStyle, customSqlDialogWidth, customSqlDividerClass,
  customSqlGeneralInlineClass, customSqlQueryListClass, customSqlQuerySublistClass, customSqlQueryTextClass, customSqlSingleCopyButtonClass, customSqlWorkbookInlineClass
} from './CustomSqlStyles';

export type CustomSQLTable = CustomSqlFragment_fields_ColumnField_columns_table_CustomSQLTable;

type CustomSqlDialogProps = {
  readonly customSQLTables: ReadonlyArray<CustomSQLTable>;
  readonly selectedDropdownName?: string | null;
  readonly onClose: () => void;
}

export function CustomSqlDialog(props: CustomSqlDialogProps): JSX.Element {
  const title: string = props.selectedDropdownName
    ? `${Messages.CustomSqlDialogTitle()}: ${props.selectedDropdownName}`
    : Messages.CustomSqlDialogTitle();

  return (
    <DialogWidget
      testId={'Custom-Sql-Dialog'}
      title={title}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Draggable}
      showCloseButton={true}
      isModal={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      dialogWidth={customSqlDialogWidth}
      dialogHeight={customSqlDialogHeight}
      dialogBodyStyle={customSqlDialogStyle}
      footerContent={props.customSQLTables.length === 1 ? <RenderDialogFooter customSQLTables={props.customSQLTables} /> : null}
    >
      {props.customSQLTables.map((table, index) =>
        <ul key={index} className={customSqlQueryListClass}>
          <li>{table.name}
            <ul className={customSqlQuerySublistClass}>
              <RenderCustomSqlQueryText queryText={table.query} />
            </ul>
          </li>
          {props.customSQLTables.length > 1 ?
            <CopyToClipboard queryText={props.customSQLTables[index].query} queryName={props.customSQLTables[index].name} />
            : null
          }
          {index < props.customSQLTables.length - 1 ? <div className={customSqlDividerClass} /> : null}
        </ul>
      )}
    </DialogWidget>
  );
}

function CopyToClipboard(props: {queryText: string | null, queryName: string | null}): JSX.Element {
  const onClick = useClipboard(props.queryText, props.queryName);

  return (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      testId={'CustomSql-Dialog-Copy'}
      onClick={onClick}
      style={customSqlCopyButtonStyle}
    >
      {Messages.DialogCopy()}
    </ButtonWidget>
  );
}

function RenderDialogFooter(props: { customSQLTables: ReadonlyArray<CustomSQLTable> }): JSX.Element {
  const onClick = useClipboard(props.customSQLTables[0].query, props.customSQLTables[0].name);

  return (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      testId={'CustomSql-Dialog-Footer-Copy'}
      onClick={onClick}
      className={customSqlSingleCopyButtonClass}
    >
      {Messages.DialogCopy()}
    </ButtonWidget>
  );
}

function RenderCustomSqlQueryText(props: { queryText: string | null }): JSX.Element {
  return (
    <pre className={customSqlQueryTextClass}>
      <code className={codeTextClass}>
        {props.queryText}
      </code>
    </pre>
  );
}

type CustomSqlTextLinkContentType =
  FlowCustomSqlQuery_flows['__typename'] |
  PublishedDatasourceCustomSqlQuery_publishedDatasources['__typename'] |
  WorkbookCustomSqlQuery_workbooks['__typename'] | VirtualConnectionQuery_virtualConnections['__typename']

export type CustomSqlTextLinkProps = {
  readonly containsUnsupportedCustomSql: boolean;
  readonly customSQLTables: ReadonlyArray<CustomSQLTable>;
  readonly contentType: CustomSqlTextLinkContentType;
  readonly selectedDropdownName?: string | null;
};

export function CustomSqlTextLink(props: CustomSqlTextLinkProps): JSX.Element {
  const [setIsDialogShowing, showDialog] = React.useState<boolean>(false);
  const queryInfoButton =
    <ButtonWidget
      buttonType={ButtonType.Outline}
      onClick={() => showDialog(true)}
      testId={'Custom-Sql-Text'}
    >
      {Messages.CustomSqlButtonTitle()}
    </ButtonWidget>

  return (
    <div>
      <InlineAlertWidget
        button={queryInfoButton}
        testId={'Custom-SQL-Text-Alert'}
        type={props.containsUnsupportedCustomSql ? InlineAlertType.Warning : InlineAlertType.Info}
        className={props.contentType === 'Workbook' || props.contentType === 'Flow' ? customSqlWorkbookInlineClass : customSqlGeneralInlineClass}
      >
        {props.containsUnsupportedCustomSql ? warningInlineText(props.contentType) : infoInlineText(props.contentType)}
      </InlineAlertWidget>

      {setIsDialogShowing &&
        <CustomSqlDialog
          customSQLTables={props.customSQLTables}
          onClose={() => showDialog(false)}
          selectedDropdownName={props.selectedDropdownName}
        />
      }
    </div>
  );
}

function infoInlineText(contentType: CustomSqlTextLinkContentType): string {
  switch (contentType) {
    case 'Workbook':
      return Messages.CustomSqlInfoInlineEmbeddedDatasource();
    case 'Flow':
      return Messages.CustomSqlInfoInlineFlow();
    case 'PublishedDatasource':
      return Messages.CustomSqlInfoInlinePublishedDatasource();
    case 'VirtualConnection':
      return Messages.CustomSqlInfoInlineVirtualConnection();
    default:
      assertUnreachable(contentType);
      return '';
  }
}

function warningInlineText(contentType: CustomSqlTextLinkContentType): string {
  switch (contentType) {
    case 'Workbook':
      return Messages.CustomSqlWarningInlineEmbeddedDatasource();
    case 'Flow':
      return Messages.CustomSqlWarningInlineFlow();
    case 'PublishedDatasource':
      return Messages.CustomSqlWarningInlinePublishedDatasource();
    case 'VirtualConnection':
      return Messages.CustomSqlInfoInlineVirtualConnection();
    default:
      assertUnreachable(contentType);
      return '';
  }
}

function useClipboard(queryText: string | null, queryName: string | null): React.MouseEventHandler<HTMLButtonElement> {
  const { popToast } = React.useContext(UIActionsContext);
  return React.useCallback(async (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    try {
      await clipboard.writeText(queryText ?? '');
      popToast('success', !queryName ? '' : Messages.CustomSqlSuccessToast({ queryName: queryName }));
    } catch (_) {
      popToast('error', Messages.CustomSqlFailureToast());
    }
  }, [queryText, queryName, popToast]);
}
